import { useEffectOnce } from 'react-use';
import {
  Box,
  Card,
  Checkbox,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  List,
  Paragraph,
  Radio,
  Text,
} from '@zillow/constellation-10';
import { Icon } from '@zillow/constellation-10';
import { IconCalendarClockFilled, IconTrendingFilled, IconTrophyFilled } from '@zillow/constellation-icons';
import { PaymentsRequestIcon } from './2PaymentsRequestIcon';
import { FIELD_IDS } from '../../../../constants/wizard';
import { Controller, useFormContext } from 'react-hook-form';
import { isValidNonNull } from '../../../../utils/validation';
import {
  emitPaymentRequestCheckEvent,
  emitPaymentRequestNoEvent,
  emitPaymentRequestYesEvent,
  emitViewPaymentRequestStepEvent,
} from '../../../../analytics/clickstreamEvents';
import { usePaymentRequestTreatment } from '../../../../utils/ab-tests';
import { useEffect } from 'react';
import type { Answers } from '../../../../graphql/queries';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';

export function PaymentRequestStep() {
  const {
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const applicationContext = useApplicationContextOrUndefined();
  const test = usePaymentRequestTreatment();

  useEffectOnce(() => {
    emitViewPaymentRequestStepEvent(applicationContext);
  });

  // Initialize requestZillowPayments field when component mounts
  useEffect(() => {
    if (test !== 'on_1a') {
      // Only initialize to false on the "checkbox" variant,
      // since it's the one that is not required, so we run
      // the risk of the user not clicking the checkbox so the endpoint
      // will not be called.

      // We don't need to manually alter the value for the "radio" variant
      // since we don't want to show any option selected initially
      return;
    }
    const currentValue = getValues(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS);
    // Only initialize to false if the field is currently null/undefined
    // This ensures we don't override an existing user choice
    if (currentValue == null) {
      setValue(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS as keyof Answers, false, {
        shouldDirty: false, // Don't mark as dirty since this is initialization
        shouldTouch: false, // Don't mark as touched since user hasn't interacted
      });
    }
  }, [getValues, setValue]);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', alignItems: 'flex-start' }}>
      <PaymentsRequestIcon height={75} />
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt">
        Do you want to build credit when you pay rent on time?
      </Heading>
      <List.Root appearance="none">
        <List.Item>
          <Text>
            <Icon>
              <IconCalendarClockFilled />
            </Icon>{' '}
            Zillow reports on time payments to Experian, a major credit bureau.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Icon>
              <IconTrendingFilled />
            </Icon>{' '}
            You could build your credit in just a few months.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            <Icon>
              <IconTrophyFilled />
            </Icon>{' '}
            Building credit can help you qualify for future loans, buy a home, and more.
          </Text>
        </List.Item>
      </List.Root>
      <Paragraph>Showing your interest now will not affect your application.</Paragraph>
      {test === 'on_1a' && (
        <Controller
          name={FIELD_IDS.REQUEST_ZILLOW_PAYMENTS}
          render={({ field: { onBlur, value, onChange } }) => (
            <LabeledControl
              onBlur={onBlur}
              label={<Label>Ask landlord to use Zillow payments if application is accepted</Label>}
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => {
                    onChange(e.target.checked);
                    if (e.target.checked) {
                      emitPaymentRequestCheckEvent(applicationContext);
                    }
                  }}
                />
              }
            />
          )}
        />
      )}

      {test === 'on_1b' && (
        <Controller
          name={FIELD_IDS.REQUEST_ZILLOW_PAYMENTS}
          rules={{ validate: isValidNonNull }}
          render={({ field: { onBlur, value, onChange } }) => (
            <FieldSet
              onBlur={onBlur}
              legend={<Legend>Request Zillow payments</Legend>}
              error={!!errors[FIELD_IDS.REQUEST_ZILLOW_PAYMENTS]}
              description={
                errors[FIELD_IDS.REQUEST_ZILLOW_PAYMENTS] && (
                  <FormHelp>{String(errors[FIELD_IDS.REQUEST_ZILLOW_PAYMENTS].message)}</FormHelp>
                )
              }
            >
              <LabeledControl
                onChange={() => {
                  onChange(true);
                  emitPaymentRequestYesEvent(applicationContext);
                }}
                label={<Label>Yes, ask landlord to use Zillow payments if application is accepted</Label>}
                control={<Radio name="zillow-payments-opt-in" defaultChecked={value} />}
              />

              <LabeledControl
                onChange={() => {
                  onChange(false);
                  emitPaymentRequestNoEvent(applicationContext);
                }}
                label={<Label>No, not interested at this time</Label>}
                control={<Radio name="zillow-payments-opt-in" defaultChecked={value === false} />}
              />
            </FieldSet>
          )}
        />
      )}
    </Box>
  );
}

export function PaymentRequestTip() {
  return (
    <Card elevated={false} tone="neutral">
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt" css={{ marginBottom: 'default' }}>
        The benefits of Zillow payments
      </Heading>
      <List.Root>
        <List.Item>
          <strong>More ways to pay </strong>
          with your debit/credit card or bank account.
        </List.Item>
        <List.Item>
          <strong>Pay on the go, </strong>
          or schedule your payments on time.
        </List.Item>
        <List.Item>
          <strong>Security is our top priority, </strong>
          and we never sell your information.
        </List.Item>
      </List.Root>
    </Card>
  );
}
